export const JAPANESE_LANGUAGE = 'ja';
// Kana, kanji, and full-width punctuation that already counts as Japanese
const JA = String.raw`[\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Han}ー々〆〇、。，．：；！？（）「」『』［］【】…・〜]`;
// Stands in for a {{placeholder}} or HTML tag while the rules run, so they are never changed
// and rules can look through them ("<b>注意</b>:" → "<b>注意</b>：")
const MASK = '\uE000';
const AFTER_JA = `(?<=${JA}${MASK}*)`;
const BEFORE_JA = `(?=${MASK}*${JA})`;

const HAS_KANA_OR_KANJI = /[\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Han}]/u;
const HAS_JA = new RegExp(JA, 'u');
const ENDS_WITH_JA = new RegExp(`${JA}${MASK}*$`, 'u');
const STARTS_WITH_JA = new RegExp(`^${MASK}*${JA}`, 'u');

const PLACEHOLDER = /\{\{[^}]*\}\}|<\/?[a-zA-Z][^<>]*>/g;
// Pairs include the spaces before them, so "名前 (必須)" → "名前（必須）"
const QUOTED = / *"([^"]*)"/g;
const PARENS = / *\(([^()]*)\)/g;
const SQUARE_BRACKETS = / *\[([^[\]]*)\]/g;
// "..." / "......" next to Japanese text
const ELLIPSIS = new RegExp(`${AFTER_JA}\\.{2,}|\\.{2,}${BEFORE_JA}`, 'gu');
// Marks after Japanese text ("?", "?!"), plus the spaces after them; a dot before a letter
// or digit ("ファイル.txt", "第1.5版") is not a full stop
const SENTENCE_MARKS = new RegExp(`${AFTER_JA}((?:[,:;!?]|\\.(?!${MASK}*[A-Za-z0-9]))+) *`, 'gu');

const FULL_WIDTH_MARKS: Record<string, string> = {
  ',': '、',
  '.': '。',
  ':': '：',
  ';': '；',
  '!': '！',
  '?': '？',
};

/**
 * Converts a quote or bracket pair that contains Japanese text, follows it ("スキップ (3)") or is
 * directly followed by it ("(Save)を"). Only the innermost of nested pairs.
 */
function pairToFullWidth(text: string, pattern: RegExp, open: string, close: string): string {
  return text.replace(pattern, (match: string, inner: string, offset: number) => {
    const touchesJapanese =
      HAS_JA.test(inner) ||
      ENDS_WITH_JA.test(text.slice(0, offset)) ||
      STARTS_WITH_JA.test(text.slice(offset + match.length));
    return touchesJapanese ? `${open}${inner}${close}` : match;
  });
}

export function normalizeJapanesePunctuation(text: string): string {
  if (!text || !HAS_KANA_OR_KANJI.test(text)) return text;

  // 1. Hide placeholders and HTML tags
  const placeholders: string[] = [];
  let result = text.replace(PLACEHOLDER, (placeholder) => {
    placeholders.push(placeholder);
    return MASK;
  });

  // 2. "Save"をクリック → 「Save」をクリック
  result = pairToFullWidth(result, QUOTED, '「', '」');

  // 3. (PNG) → （PNG）, [注意] → ［注意］
  result = pairToFullWidth(result, PARENS, '（', '）');
  result = pairToFullWidth(result, SQUARE_BRACKETS, '［', '］');

  // 4. 読み込み中... → 読み込み中…
  result = result.replace(ELLIPSIS, (dots) => '…'.repeat(Math.max(1, Math.round(dots.length / 3))));

  // 5. 完了. / はい, / 本当? → 。 、 ？
  result = result.replace(SENTENCE_MARKS, (_match, marks: string) =>
    Array.from(marks, (mark) => FULL_WIDTH_MARKS[mark]).join('')
  );

  // 6. Restore placeholders and HTML tags
  return result.replace(new RegExp(MASK, 'g'), () => placeholders.shift()!);
}

export function normalizeJapaneseTranslations<T>(translations: T): T {
  if (typeof translations === 'string') return normalizeJapanesePunctuation(translations) as T;
  if (Array.isArray(translations)) return translations.map(normalizeJapaneseTranslations) as T;
  if (translations && typeof translations === 'object') {
    return Object.fromEntries(
      Object.entries(translations).map(([key, value]) => [key, normalizeJapaneseTranslations(value)])
    ) as T;
  }
  return translations;
}

export function formatLabel(text: string, lang: string, value?: string | number): string {
  if (!text) return text;
  const hasValue = value !== undefined && value !== null && value !== '';
  if (lang !== JAPANESE_LANGUAGE) {
    return hasValue ? `${text}: ${value}` : `${text}:`;
  }
  const label = `${text.replace(/\s*[:：]\s*$/, '')}：`;
  return hasValue ? `${label}${value}` : label;
}

/** "..." / "…" */
export function ellipsis(lang: string): string {
  return lang === JAPANESE_LANGUAGE ? '…' : '...';
}

/** " (5)" / "（5）" */
export function formatParenthetical(text: string | number, lang: string): string {
  if (text === null || text === undefined || text === '') return '';
  return lang === JAPANESE_LANGUAGE ? `（${text}）` : ` (${text})`;
}

/** "A, B" / "A、B" */
export function formatList(items: readonly (string | null | undefined)[], lang: string): string {
  return (items ?? []).join(lang === JAPANESE_LANGUAGE ? '、' : ', ');
}
